import {
    TextalignCenter,
    TextalignLeft,
    TextalignRight,
    SeparatorHorizontal,
} from '@/components/icons';
import { Checkbox } from '@/components/ui/checkbox';
import CollapsibleSection from '@/components/ui/collapsible-section';
import { Label } from '@/components/ui/label';
import { resolveDividerBackground, summarizeDivider } from '../summarize';
import type { DividerCustomizations } from '@/components/preview/types';

interface DividerSectionProps {
    open: boolean;
    onToggle: () => void;
    config: DividerCustomizations;
    updateConfig: (updates: Partial<DividerCustomizations>) => void;
    /** How many `divider` blocks this laudo actually has. */
    blockCount: number;
}

/**
 * The titles that group instruments inside Resultados dos Testes — "Atenção",
 * "Memória". Colour moved to the Cores section; what stays here is layout.
 */
export function DividerSection({
    open,
    onToggle,
    config,
    updateConfig,
    blockCount,
}: DividerSectionProps) {
    // A line under a filled title is the report colour drawn on the report
    // colour. The control only means something over a transparent background,
    // which is set one section up, in Cores › Títulos de grupo.
    const isTransparent = resolveDividerBackground(config) === null;

    return (
        <CollapsibleSection
            title="Títulos de grupo"
            testId="customize-section-divider"
            icon={SeparatorHorizontal}
            summary={summarizeDivider(config)}
            open={open}
            onToggle={onToggle}
        >
            {blockCount === 0 && (
                <p className="text-xs text-muted-foreground">
                    Este laudo ainda não tem títulos de grupo. O ajuste vale para os que você
                    adicionar.
                </p>
            )}

            {/* Text Alignment */}
            <div className="space-y-2">
                <Label className="text-sm font-medium">Alinhamento do Texto</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                    {(
                        [
                            {
                                value: 'left' as const,
                                label: 'Esquerda',
                                Icon: TextalignLeft,
                            },
                            {
                                value: 'center' as const,
                                label: 'Centro',
                                Icon: TextalignCenter,
                            },
                            {
                                value: 'right' as const,
                                label: 'Direita',
                                Icon: TextalignRight,
                            },
                        ] as const
                    ).map(({ value, label, Icon }) => (
                        <button
                            key={value}
                            onClick={() => updateConfig({ alignment: value })}
                            className={`flex flex-col items-center gap-2 rounded-lg border-2 p-3 transition-all ${
                                (config.alignment || 'center') === value
                                    ? 'border-[#00006B] bg-[#00006B] text-white'
                                    : 'border-border bg-card text-foreground hover:border-border-strong'
                            }`}
                        >
                            <Icon className="size-4" />
                            <span className="text-xs font-medium">{label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Show Line — hidden while the title carries a background */}
            {isTransparent && (
                <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                    <Checkbox
                        id="divider-show-line"
                        checked={config.show_line !== false}
                        onCheckedChange={(checked) => updateConfig({ show_line: !!checked })}
                        className="mt-0.5"
                    />
                    <div className="flex-1">
                        <Label
                            htmlFor="divider-show-line"
                            className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                        >
                            <SeparatorHorizontal className="size-4 text-muted-foreground" />
                            Mostrar linha abaixo
                        </Label>
                    </div>
                </div>
            )}

            {/* Font Style */}
            <div className="space-y-2">
                <Label className="text-sm font-medium">Estilo da Fonte</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                    <button
                        onClick={() => updateConfig({ font_style: 'normal' })}
                        className={`flex flex-col items-center gap-2 rounded-lg border-2 p-3 transition-all ${
                            (config.font_style || 'normal') === 'normal'
                                ? 'border-[#00006B] bg-[#00006B] text-white'
                                : 'border-border bg-card text-foreground hover:border-border-strong'
                        }`}
                    >
                        <span className="text-xs font-medium">Normal</span>
                    </button>
                    <button
                        onClick={() => updateConfig({ font_style: 'bold' })}
                        className={`flex flex-col items-center gap-2 rounded-lg border-2 p-3 transition-all ${
                            config.font_style === 'bold'
                                ? 'border-[#00006B] bg-[#00006B] text-white'
                                : 'border-border bg-card text-foreground hover:border-border-strong'
                        }`}
                    >
                        <span className="text-xs font-bold">Negrito</span>
                    </button>
                    <button
                        onClick={() => updateConfig({ font_style: 'italic' })}
                        className={`flex flex-col items-center gap-2 rounded-lg border-2 p-3 transition-all ${
                            config.font_style === 'italic'
                                ? 'border-[#00006B] bg-[#00006B] text-white'
                                : 'border-border bg-card text-foreground hover:border-border-strong'
                        }`}
                    >
                        <span className="text-xs font-medium italic">Itálico</span>
                    </button>
                </div>
            </div>
        </CollapsibleSection>
    );
}
